import React, { useEffect, useRef, useState } from 'react';
import {
  FlaskConical, BookOpen, Microscope, Atom, Cog, Lightbulb,
  Sparkles, ExternalLink, ArrowRight, GraduationCap, Trophy,
  Brain, Camera,
} from 'lucide-react';

const BRAND = '#e4376f';

const Pillar = ({ icon: Icon, title, desc, color }) => (
  <div className="group relative flex gap-3 p-3.5 rounded-xl bg-white border border-slate-200 hover:border-rose-200 hover:shadow-md hover:shadow-rose-100 hover:-translate-y-0.5 transition-all">
    <div
      className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-white shadow-sm"
      style={{ background: color }}
    >
      <Icon className="w-5 h-5" strokeWidth={2.4} />
    </div>
    <div className="min-w-0">
      <p className="font-bold text-slate-900 text-sm leading-tight">{title}</p>
      <p className="text-xs text-slate-500 mt-1 leading-snug">{desc}</p>
    </div>
  </div>
);

const FloatingIcon = ({ icon: Icon, className, scrollOffset = 0, parallaxFactor = 0.3 }) => (
  <div
    className={`absolute rounded-2xl bg-white/15 backdrop-blur-md flex items-center justify-center border border-white/30 shadow-xl shadow-black/10 animate-float ${className}`}
    style={{
      transform: `translateY(${scrollOffset * parallaxFactor}px)`,
      transition: 'transform 0.1s linear',
    }}
  >
    <Icon className="text-white w-1/2 h-1/2" strokeWidth={2} />
  </div>
);

/**
 * Scroll-linked parallax: tracks how far the element has moved from viewport center.
 * Returns a single scroll offset that floating icons multiply by their own parallax factor.
 */
const useParallax = () => {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (!ref.current) return;
    let raf = null;
    const handle = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const viewportCenter = window.innerHeight / 2;
        const elementCenter = rect.top + rect.height / 2;
        // Positive when element is above center, negative when below
        setOffset(viewportCenter - elementCenter);
      });
    };
    handle();
    window.addEventListener('scroll', handle, { passive: true });
    window.addEventListener('resize', handle);
    return () => {
      window.removeEventListener('scroll', handle);
      window.removeEventListener('resize', handle);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return [ref, offset];
};

const AboutNischalsSection = () => {
  const [leftRef, scrollOffset] = useParallax();

  return (
    <section className="mb-20" aria-label="About nischals">
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-8px) rotate(2deg); }
        }
        .animate-float { animation: float 4s ease-in-out infinite; }
      `}</style>

      <div className="relative max-w-5xl mx-auto">
        <div
          className="absolute -inset-1 rounded-[2rem] blur-xl opacity-30"
          style={{ background: 'linear-gradient(135deg, #e4376f, #fbbf24, #10b981, #6366f1)' }}
        />

        <div className="relative grid grid-cols-1 lg:grid-cols-5 rounded-3xl overflow-hidden bg-white shadow-2xl shadow-rose-200/40 border border-white">

          {/* LEFT — Visual story panel with parallax */}
          <div
            ref={leftRef}
            className="relative overflow-hidden lg:col-span-2 p-8 sm:p-10 text-white min-h-[360px] lg:min-h-[480px]"
            style={{ background: `linear-gradient(135deg, ${BRAND} 0%, #c11d50 60%, #6a1235 100%)` }}
          >
            <svg className="absolute inset-0 w-full h-full opacity-10" aria-hidden="true">
              <defs>
                <pattern id="aboutDots" width="22" height="22" patternUnits="userSpaceOnUse">
                  <circle cx="2" cy="2" r="1.6" fill="white" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#aboutDots)" />
            </svg>

            <div className="absolute -top-16 -left-16 w-56 h-56 bg-amber-300/25 rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -right-12 w-60 h-60 bg-fuchsia-300/20 rounded-full blur-3xl" />

            {/* Floating science icons with scroll-linked parallax. Each gets a different factor for depth. */}
            <FloatingIcon icon={FlaskConical} className="hidden sm:flex w-14 h-14 top-6 right-8" scrollOffset={scrollOffset} parallaxFactor={0.18} />
            <FloatingIcon icon={Atom} className="hidden sm:flex w-12 h-12 top-32 right-32" scrollOffset={scrollOffset} parallaxFactor={-0.22} />
            <FloatingIcon icon={Microscope} className="hidden sm:flex w-16 h-16 bottom-16 right-10" scrollOffset={scrollOffset} parallaxFactor={0.28} />
            <FloatingIcon icon={Cog} className="hidden sm:flex w-10 h-10 top-20 left-10" scrollOffset={scrollOffset} parallaxFactor={-0.16} />
            <FloatingIcon icon={Lightbulb} className="hidden sm:flex w-11 h-11 bottom-24 left-16" scrollOffset={scrollOffset} parallaxFactor={0.22} />

            <div className="relative h-full flex flex-col justify-end">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur border border-white/20 w-fit mb-4">
                <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                <span className="text-[10px] font-bold tracking-widest uppercase text-white/90">Since 2010</span>
              </div>
              <h3 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-[1.05] mb-3">
                Where curiosity <br />
                <span className="text-amber-300">becomes knowledge.</span>
              </h3>
              <p className="text-white/80 text-sm sm:text-base leading-relaxed max-w-sm">
                For over a decade, nischals has been making science come alive in classrooms across the world.
              </p>
            </div>
          </div>

          {/* RIGHT — Product pillars + CTA */}
          <div className="lg:col-span-3 p-7 sm:p-10 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="h-px w-8" style={{ background: BRAND }} />
                <span className="text-xs font-bold uppercase tracking-widest" style={{ color: BRAND }}>
                  Beyond EduLab Monitor
                </span>
              </div>
              <h4 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-2">
                The complete nischals ecosystem
              </h4>
              <p className="text-sm text-slate-600 mb-6">
                EduLab Monitor is just one piece. Discover the full lineup of products that make
                nischals India's most loved hands-on learning brand.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-7">
                <Pillar
                  icon={FlaskConical}
                  title="Portable & Micro-Scale Labs"
                  desc="Take real lab experiments anywhere"
                  color="linear-gradient(135deg, #e4376f, #c11d50)"
                />
                <Pillar
                  icon={GraduationCap}
                  title="Smart Student Kits"
                  desc="Curriculum-aligned hands-on kits"
                  color="linear-gradient(135deg, #6366f1, #4f46e5)"
                />
                <Pillar
                  icon={Trophy}
                  title="Excellence Program"
                  desc="Olympiad & STEM enrichment"
                  color="linear-gradient(135deg, #f59e0b, #d97706)"
                />
                <Pillar
                  icon={Brain}
                  title="Minds Sports"
                  desc="Strategic thinking & logic games"
                  color="linear-gradient(135deg, #8b5cf6, #6d28d9)"
                />
                <Pillar
                  icon={BookOpen}
                  title="Beyond Curriculum Reads"
                  desc="Story-driven science books"
                  color="linear-gradient(135deg, #10b981, #047857)"
                />
                <Pillar
                  icon={Camera}
                  title="nischals Lens — AR App"
                  desc="Augmented-reality science experiences"
                  color="linear-gradient(135deg, #0ea5e9, #0369a1)"
                />
              </div>
            </div>

            <a
              href="https://www.nischalsworld.com/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="explore-nischalsworld-cta"
              className="group inline-flex items-center justify-center sm:justify-between gap-2 px-5 py-3.5 rounded-2xl text-white font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all relative overflow-hidden"
              style={{
                background: `linear-gradient(135deg, ${BRAND} 0%, #c11d50 100%)`,
                boxShadow: '0 12px 28px -10px rgba(228, 55, 111, 0.55)',
              }}
            >
              <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
              <span className="relative flex items-center gap-2">
                <span>Explore nischalsworld.com</span>
              </span>
              <span className="relative flex items-center gap-2 text-white/90">
                <span className="hidden sm:inline text-xs uppercase tracking-widest font-bold">Visit</span>
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                <ExternalLink className="w-4 h-4 opacity-70" />
              </span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutNischalsSection;
